import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { Modal } from "components/designV2/modal/modal";
import { TextArea } from "components/designV2/textarea/textarea";
import { ColorV2 } from "config/colors";
import { useReportAbsence } from "hooks/useReportAbsence";
import { AbsenceType } from "model/Schedule";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorModal } from "web-apps/company/components/common/errorModal.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getAbsenceTypeOptions } from "web-apps/company/utils/absenceLabels";
import {
  ActionRow,
  FieldLabel,
  FieldStack,
  ModalBody,
} from "./reportAbsenceModalV2.styled";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeReportId?: number | null;
  timeReportIds?: number[];
  onReportAbsence?: (payload: {
    absenceType: AbsenceType;
    comment: string;
    succeededCount: number;
    failedCount: number;
    failedIds: number[];
  }) => void;
}

const REPORT_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.DeclinedRed,
  activeBackgroundColor: ColorV2.DeclinedRed,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

export const ReportAbsenceModalV2: React.FC<ModalProps> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const { reportAbsence, isLoading, error } = useReportAbsence();
  const companyId = companyState.company?.id;
  const [absenceType, setAbsenceType] = useState<AbsenceType | "">("");
  const [comment, setComment] = useState("");
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const effectiveTimeReportIds = useMemo(() => {
    const reportIds = props.timeReportIds ?? [];
    if (reportIds.length > 0) {
      const validIds = reportIds.filter(
        (value) => Number.isInteger(value) && value > 0,
      );
      return Array.from(new Set(validIds));
    }
    if (props.timeReportId == null) return [];
    return Number.isInteger(props.timeReportId) && props.timeReportId > 0
      ? [props.timeReportId]
      : [];
  }, [props.timeReportId, props.timeReportIds]);

  useEffect(() => {
    if (props.isOpen) return;
    setIsErrorOpen(false);
    setAbsenceType("");
    setComment("");
  }, [props.isOpen]);

  const absenceOptions = useMemo(
    (): Array<{ value: AbsenceType; label: string }> =>
      getAbsenceTypeOptions(t),
    [t],
  );

  const handleAbsenceChange = (value?: string | null): void => {
    if (!value) return;
    const selected = absenceOptions.find((option) => option.value === value);
    if (!selected) return;
    setAbsenceType(selected.value);
  };

  const handleClose = (): void => {
    setIsErrorOpen(false);
    props.onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!absenceType || !companyId || effectiveTimeReportIds.length === 0)
      return;
    const trimmedComment = comment.trim();
    const failedIds: number[] = [];
    let succeededCount = 0;

    for (const timeReportId of effectiveTimeReportIds) {
      const result = await reportAbsence({
        companyId,
        timeReportId,
        comment: trimmedComment,
        resolution: absenceType,
      });
      if (!result.ok) {
        failedIds.push(timeReportId);
        continue;
      }
      succeededCount += 1;
    }

    const failedCount = failedIds.length;
    if (succeededCount === 0) {
      setIsErrorOpen(true);
      return;
    }

    props.onReportAbsence?.({
      absenceType,
      comment: trimmedComment,
      succeededCount,
      failedCount,
      failedIds,
    });
    props.onClose();
  };

  return (
    <Modal
      label={t("CompanyTimeReport.ReportAbsence")}
      isOpen={props.isOpen}
      onClose={handleClose}
      size="small"
      showCloseButton={false}
      contentOverflow="visible"
      footer={
        <ActionRow>
          <SecondaryButton
            label={t("CompanyTimeReport.Cancel")}
            onClick={handleClose}
          />
          <PrimaryButton
            label={t("CompanyTimeReport.ReportAbsence")}
            iconName="v2/absent"
            styleOverrides={REPORT_BUTTON_STYLES}
            disabled={
              !absenceType ||
              !companyId ||
              effectiveTimeReportIds.length === 0 ||
              isLoading
            }
            isLoading={isLoading}
            onClick={handleSubmit}
          />
        </ActionRow>
      }
    >
      <ModalBody>
        <FieldStack>
          <FieldLabel>{t("Schedule.ReasonForAbsence")}</FieldLabel>
          <Dropdown
            options={absenceOptions}
            value={absenceType || undefined}
            onChange={handleAbsenceChange}
            placeholder={t("CompanyTimeReport.ChooseAbsence")}
            fullWidth
          />
        </FieldStack>
        <TextArea
          label={t("Schedule.Comment")}
          value={comment}
          onChange={setComment}
          placeholder={t("Schedule.CommentPlaceholder")}
          maxHeight={240}
        />
      </ModalBody>
      {error && (
        <ErrorModal
          isOpen={isErrorOpen}
          onClose={() => setIsErrorOpen(false)}
          messageKey={error}
        />
      )}
    </Modal>
  );
};
